<template>
    <div>
      <div class="content__page-header">
        <h1>{{ $t('startups.title') }}</h1>
        <p>{{ $t('startups.description') }}</p>
      </div>
  
      <!-- Filters Section -->
      <div class="filters-section">
        <el-row :gutter="20">
          <el-col :xs="24" :sm="12" :md="4">
            <el-input
              v-model="searchQuery"
              :placeholder="$t('startups.search_placeholder')"
              clearable
              class="search-input"
            >
              <template #prefix>
                <i class="el-icon-search"></i>
              </template>
            </el-input>
          </el-col>
          <el-col :xs="24" :sm="12" :md="4">
            <el-select 
              v-model="selectedCountry" 
              :placeholder="$t('startups.filter_by_country')" 
              clearable
              class="country-select"
            >
            <el-option
            v-for="country in countries"
            :key="country.code"
            :label="$t(`countries.${country.code}`, country.code)"
            :value="country.code"
            />
            </el-select>
          </el-col>
          <el-col :xs="24" :sm="12" :md="4">
            <el-select 
              v-model="selectedIndustry" 
              :placeholder="$t('startups.filter_by_industry')" 
              clearable
              class="industry-select"
            >
            <el-option
            v-for="industry in industries"
            :key="industry.id"
            :label="capitalizeWords(industry.name)"
            :value="industry.id"
            />
            </el-select>
          </el-col>
          <el-col :xs="24" :sm="12" :md="4">
            <el-select 
              v-model="selectedYear" 
              :placeholder="$t('startups.filter_by_year')" 
              clearable
              class="year-select"
            >
              <el-option
                v-for="year in years"
                :key="year"
                :label="year"
                :value="year"
              />
            </el-select>
          </el-col>
          <el-col :xs="24" :sm="12" :md="4" class="filter-actions">
            <el-button 
              @click="resetFilters" 
              type="default" 
              size="medium"
              class="clear-filters-btn"
            >
              {{ $t('startups.clear_filters') }}
            </el-button>
          </el-col>
        </el-row>
      </div>
  
      <!-- Table -->
      <div class="table-container" v-loading="loading">
        <table v-if="filteredStartups.length > 0" class="startups-table">
          <thead>
            <tr>
              <th>{{ $t('startups.logo') }}</th>
              <th>{{ $t('startups.name') }}</th>
              <th>{{ $t('startups.industry') }}</th>
              <th>{{ $t('startups.country') }}</th>
              <th>{{ $t('startups.city') }}</th>
              <th>{{ $t('startups.founded') }}</th>
              <th>{{ $t('startups.website') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr 
              v-for="startup in filteredStartups" 
              :key="startup.id || startup.name"
              class="table-row"
            >
              <td>
                <div class="startup-logo">
                  <img 
                    v-if="startup.logo_url" 
                    :src="startup.logo_url" 
                    :alt="startup.name"
                    class="logo-image"
                  />
                  <div v-else class="logo-placeholder">
                    {{ startup.name.charAt(0).toUpperCase() }}
                  </div>
                </div>
              </td>
              <td>
                <div class="name-cell">
                  {{ startup.name }}
                  <el-tooltip
                    v-if="startup.description"
                    :content="startup.description"
                    placement="right"
                    :show-after="0"
                    :hide-after="0"
                    popper-class="description-tooltip"
                    :manual="true"
                    v-model="tooltipVisible"
                  >
                    <i 
                      class="fas fa-info-circle info-icon"
                      @click="showTooltip(startup)"
                    ></i>
                  </el-tooltip>
                </div>
              </td>
              <td>{{ capitalizeWords(industryName(startup.industry_id)) }}</td>
              <td>{{ $t(`countries.${startup.country_code}`, startup.country_code) }}</td>
              <td>{{ startup.city }}</td>
              <td>{{ startup.year_founded }}</td>
              <td>
                <a
                  v-if="startup.website"
                  :href="startup.website"
                  target="_blank"
                  :class="[
                    'website-link',
                    { 'inactive-website': !startup.website_active }
                  ]"
                >
                  {{ startup.website }}
                </a>
                <span v-else>{{ $t('startups.unavailable') }}</span>
              </td>
            </tr>
          </tbody>
        </table>
        <el-empty
            v-if="!loading && filteredStartups.length === 0"
            :image-size="120"
            :description="$t('startups.no_results')"
        />
      </div>

      <el-tooltip
        v-model="tooltipVisible"
        :content="currentDescription"
        placement="right"
        :show-after="0"
        :hide-after="0"
        popper-class="description-tooltip"
        :manual="true"
      />
    </div>

    <el-dialog
  v-model="isModalVisible"
  :title="selectedStartup?.name"
  width="500px"
  center
>
  <template #default>
    <p><strong>{{ $t('startups.description_label') }}:</strong></p>
    <p>{{ selectedStartup?.description || $t('startups.no_description') }}</p>
  </template>
</el-dialog>


    
  </template>
  
  
  
  <script>
 import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import apolloClient from '@/services/apollo-client';
import { GET_STARTUPS } from '@/graphql/startups';

export default {
  name: 'StartupsPage',
  setup() {
    const { t } = useI18n();
    const startups = ref([]);
    const loading = ref(false);
    const searchQuery = ref('');
    const selectedCountry = ref('');
    const selectedIndustry = ref('');
    const isModalVisible = ref(false);
    const selectedStartup = ref(null);
    const tooltipVisible = ref(false);
    const currentDescription = ref('');
    const selectedYear = ref('');
    const countries = ref([]);
    const industries = ref([]);

    const countryName = (code) => {
      return t(`countries.${code}`, code);
    };

    const industryName = (id) => {
      const industry = industries.value.find(i => i.id == id);
      return industry?.name || '';
    };

    const openDescriptionModal = (startup) => {
      selectedStartup.value = startup;
      isModalVisible.value = true;
    };

    const capitalizeWords = (str) => {
      if (!str) return '';
      return str
        .toLowerCase()
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    };

    const resetFilters = () => {
      searchQuery.value = '';
      selectedCountry.value = '';
      selectedIndustry.value = '';
      selectedYear.value = '';
    };

    // const countries = computed(() => {
    //   const set = new Set(startups.value.map(s => s.country).filter(Boolean));
    //   return [...set].sort();
    // });

    // const industries = computed(() => {
    //   const set = new Set(startups.value.map(s => s.industry).filter(Boolean));
    //   return [...set].sort();
    // });

    const years = computed(() => {
      const set = new Set(startups.value.map(s => s.year_founded).filter(Boolean));
      return [...set].sort((a, b) => b - a); // Sort in descending order
    });

    const fetchStartups = async () => {
      loading.value = true;
      try {
        const { data } = await apolloClient.query({
          query: GET_STARTUPS,
        });
        startups.value = data.startups;
        countries.value = data.countries;
        industries.value = data.industries;
      } catch (err) {
        console.error('Error fetching startups:', err);
      } finally {
        loading.value = false;
      }
    };

    const filteredStartups = computed(() => {
      return startups.value.filter(startup => {
        const matchesSearch =
          !searchQuery.value ||
          [startup.name, startup.city, startup.description].some(field =>
            field && String(field).toLowerCase().includes(searchQuery.value.toLowerCase())
          );

          const matchesCountry = !selectedCountry.value || startup.country_code === selectedCountry.value;
          const matchesIndustry = !selectedIndustry.value || String(startup.industry_id) === String(selectedIndustry.value);
          const matchesYear = !selectedYear.value || parseInt(startup.year_founded) === parseInt(selectedYear.value);

        return matchesSearch && matchesCountry && matchesIndustry && matchesYear;
      });
    });

    const showTooltip = (startup) => {
      if (startup.description) {
        currentDescription.value = startup.description;
        tooltipVisible.value = true;
      }
    };

    const hideTooltip = () => {
      tooltipVisible.value = false;
    };

    onMounted(() => {
      fetchStartups();
    });

    return {
      startups,
      loading,
      searchQuery,
      selectedCountry,
      selectedIndustry,
      filteredStartups,
      countries,
      industries,
      resetFilters,
      capitalizeWords,
      openDescriptionModal,
      isModalVisible,
      selectedStartup,
      tooltipVisible,
      currentDescription,
      showTooltip,
      hideTooltip,
      selectedYear,
      countryName,
      industryName,
      years,
    };
  },
};


  </script>
  
  <style scoped>
    .table-container {
      margin-top: 20px;
      position: relative;
    }

    .startups-table {
      width: 100%;
      border-collapse: collapse;
      background: white;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    }

    .startups-table th,
    .startups-table td {
      padding: 12px 15px;
      text-align: left;
      border-bottom: 1px solid #ebeef5;
    }

    .startups-table th {
      background-color: #f5f7fa;
      font-weight: 600;
      color: #606266;
    }

    .table-row {
      transition: background-color 0.3s ease;
    }

    .table-row:hover {
      background-color: #f5f7fa;
    }

    .name-cell {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .info-icon {
      color: #909399;
      cursor: pointer;
      font-size: 16px;
      transition: color 0.3s ease;
    }

    .info-icon:hover {
      color: #409EFF;
    }

    .website-link {
      color: #409EFF;
      text-decoration: none;
    }

    .website-link:hover {
      text-decoration: underline;
    }

    .no-results {
      text-align: center;
      padding: 20px;
      color: #909399;
    }

    .filters-section {
      background: white;
      padding: 20px;
      margin-bottom: 30px;
      box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
    }

    .search-input,
    .country-select,
    .industry-select {
      width: 100%;
    }

    .filter-actions {
      display: flex;
      justify-content: flex-end;
      align-items: center;
    }

    @media (max-width: 768px) {
      .filter-actions {
        justify-content: center;
        margin-top: 20px;
      }
    }

    .inactive-website {
      color: #909399 !important;
      text-decoration: line-through !important;
      pointer-events: none;
      cursor: not-allowed;
    }

    .startup-logo {
      width: 40px;
      height: 40px;
      border-radius: 8px;
      overflow: hidden;
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: #f0f2f5;
    }

    .logo-image {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .logo-placeholder {
      font-size: 1.2rem;
      font-weight: 600;
      color: #409EFF;
    }

    .startup-name {
      font-weight: 600;
      color: #303133;
    }
  </style>

  <style>
    .description-tooltip {
      max-width: 400px !important;
      padding: 15px !important;
      font-size: 16px !important;
      line-height: 1.6 !important;
      background-color: #000 !important;
      color: #fff !important;
      border: none !important;
      box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.3) !important;
      white-space: pre-wrap !important;
      word-wrap: break-word !important;
    }

    .description-tooltip .el-tooltip__arrow {
      border-top-color: #000 !important;
    }
  </style>
  